import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ApplicationCode } from '@ph-env/model/applications.models';
import { pipe } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { Env } from '@ph-core/enums/environment';
import { ProductViewType } from '@ph-core/enums/product-view-type';
import { ConfigService } from '@ph-core/services/config/config.service';
import { NotificationService } from '@ph-core/services/notification';
import { withConditionalStorageSync } from '@ph-core/utils/with-conditional-storage-sync';
import { ClientConfigResponse } from '@ph-model/api/response/client-config/client-config.response.model';
import { PhNotificationType } from '@ph-shared/components/notification/notification/notification.interface';

const storeKey = 'ConfigState';
const environment = ConfigService.getEnvironment();

export interface ConfigState {
  application: ApplicationCode | undefined;
  clientConfig: ClientConfigResponse;
  dealerView: boolean;
  error: string;
  initialClientConfig: ClientConfigResponse;
  initialLoading: boolean;
  success: boolean;
  templateServiceLoading: boolean;
}

const InitialState: ConfigState = {
  application: undefined,
  clientConfig: new ClientConfigResponse(),
  dealerView: environment.features.shopping.defaultProductViewType === ProductViewType.Dealer,
  error: '',
  initialClientConfig: new ClientConfigResponse(),
  initialLoading: false,
  success: false,
  templateServiceLoading: false,
};

export type ConfigStoreType = InstanceType<typeof ConfigStore>;

export const ConfigStore = signalStore(
  { providedIn: 'root' },
  withDevtools(storeKey),
  withConditionalStorageSync({ key: storeKey }),
  withState<ConfigState>(InitialState),
  withMethods((store, httpClient = inject(HttpClient), notificationService = inject(NotificationService)) => ({
    getInitialLayout: rxMethod<{ application: ApplicationCode }>(
      pipe(
        switchMap(({ application }: { application: ApplicationCode }) => {
          patchState(store, { error: undefined, application, initialLoading: true });

          return httpClient
            .get<ClientConfigResponse>(`${environment.apiUrl}/getclientinfo`, {
              params: { application: application === 'sginternal' ? 'bmwprotectioncom' : application },
            })
            .pipe(
              tapResponse<ClientConfigResponse>({
                next: (clientConfigResponse: ClientConfigResponse) => {
                  if (
                    environment.type !== Env.PROD &&
                    (!clientConfigResponse.applicationId || !clientConfigResponse.programId)
                  ) {
                    notificationService.push({
                      type: PhNotificationType.info,
                      value: {
                        title: 'API is not configured',
                        message: clientConfigResponse.message ?? 'Application ID not found',
                      },
                    });
                  }

                  const updatedClientConfigResponse = {
                    ...clientConfigResponse,
                    css: { ...clientConfigResponse.css },
                  };

                  patchState(store, {
                    initialLoading: false,
                    clientConfig: updatedClientConfigResponse,
                    initialClientConfig: updatedClientConfigResponse,
                    success: true,
                    error: undefined,
                  });
                },
                error: (err) =>
                  patchState(store, {
                    error: err?.toString() ?? 'Error fetching client config',
                    success: false,
                    initialLoading: false,
                  }),
              })
            );
        })
      )
    ),

    toggleDealerView(dealerView: boolean): void {
      patchState(store, () => ({ dealerView }));
    },

    setTemplateServiceLoading(): void {
      patchState(store, () => ({ templateServiceLoading: true }));
    },

    resetTemplateServiceLoading(): void {
      patchState(store, () => ({ templateServiceLoading: false }));
    },
  }))
);
